import {
  DefaultMetricOptions,
  Goal,
  GoalProgressCount,
  IconFormNames,
  ID,
  LifeArea,
  RoleOption,
  Roles,
  SortingMode,
  Timestamp,
} from '@griply/types';
import { addMonths, endOfMonth, startOfMonth, startOfYear } from 'date-fns';

const previewRoles: Roles = {
  [RoleOption.Owner]: 'preview',
  [RoleOption.All]: ['preview'],
};

type PreviewLifeAreaConfig = {
  id: ID;
  name: string;
  color: string;
  iconName: IconFormNames;
};

const LIFE_AREAS: PreviewLifeAreaConfig[] = [
  {
    id: 'preview-life-area-work',
    name: 'Work & Career',
    color: '#6245B6',
    iconName: IconFormNames.Rocket,
  },
  {
    id: 'preview-life-area-health',
    name: 'Sport & Health',
    color: '#E8743B',
    iconName: IconFormNames.Heartbeat,
  },
  {
    id: 'preview-life-area-money',
    name: 'Money & Finance',
    color: '#2BB673',
    iconName: IconFormNames.CoinsDocumentChart,
  },
  {
    id: 'preview-life-area-personal',
    name: 'Personal development',
    color: '#3B82F6',
    iconName: IconFormNames.BookOpen,
  },
];

type PreviewGoalConfig = {
  id: ID;
  name: string;
  lifeAreaId: ID;
  iconName: IconFormNames;
  // months after year start + inclusive span, so bars anchor to the visible year
  startMonth: number;
  durationMonths: number;
  // 0–100 fake completion driving the bar's progress ring
  progress: number;
};

const GOALS: PreviewGoalConfig[] = [
  {
    id: 'preview-goal-sidebusiness',
    name: 'Start sidebusiness',
    lifeAreaId: 'preview-life-area-work',
    iconName: IconFormNames.Rocket,
    startMonth: 1,
    durationMonths: 5,
    progress: 60,
  },
  {
    id: 'preview-goal-website',
    name: 'Launch website',
    lifeAreaId: 'preview-life-area-work',
    iconName: IconFormNames.GroupPeople,
    startMonth: 5,
    durationMonths: 2,
    progress: 25,
  },
  {
    id: 'preview-goal-marathon',
    name: 'Run a marathon',
    lifeAreaId: 'preview-life-area-health',
    iconName: IconFormNames.SportSneakers,
    startMonth: 0,
    durationMonths: 9,
    progress: 45,
  },
  {
    id: 'preview-goal-lose-weight',
    name: 'Lose 11 pounds',
    lifeAreaId: 'preview-life-area-health',
    iconName: IconFormNames.SportDumbbell,
    startMonth: 2,
    durationMonths: 5,
    progress: 70,
  },
  {
    id: 'preview-goal-invest',
    name: 'Invest $50k',
    lifeAreaId: 'preview-life-area-money',
    iconName: IconFormNames.CoinsDocumentChart,
    startMonth: 0,
    durationMonths: 12,
    progress: 35,
  },
  {
    id: 'preview-goal-read-books',
    name: 'Read 12 books',
    lifeAreaId: 'preview-life-area-personal',
    iconName: IconFormNames.BookOpen,
    startMonth: 0,
    durationMonths: 11,
    progress: 50,
  },
];

const makePreviewLifeArea = (
  config: PreviewLifeAreaConfig,
  now: Timestamp,
): LifeArea => ({
  id: config.id,
  name: config.name,
  color: config.color,
  iconName: config.iconName,
  roles: previewRoles,
  templateId: null,
  createdAt: now,
  updatedAt: now,
  lifescanValue: null,
  sections: null,
  goalSorting: null,
  taskSorting: null,
  visionPreview: null,
  frozenAt: null,
  archivedAt: null,
});

const makePreviewGoal = (
  config: PreviewGoalConfig,
  yearStart: Timestamp,
  now: Timestamp,
): Goal => {
  const startDate = startOfMonth(addMonths(yearStart, config.startMonth));
  const deadline = endOfMonth(addMonths(startDate, config.durationMonths - 1));

  return {
    id: config.id,
    name: config.name,
    lifeAreaId: config.lifeAreaId,
    iconName: config.iconName,
    color: null,
    image: null,
    startDate,
    deadline,
    metric: {
      id: DefaultMetricOptions.CompletedTasks,
      startValue: null,
      targetValue: null,
      unit: null,
      entries: null,
    },
    taskCount: null,
    completedTaskCount: null,
    sections: null,
    ancestorIds: null,
    sortingMode: SortingMode.Custom,
    taskSorting: null,
    goalSorting: null,
    roles: previewRoles,
    frozenAt: null,
    completedAt: null,
    archivedAt: null,
    updatedAt: null,
    createdAt: now,
  };
};

export type TimelinePreviewData = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  goalProgressCounts: Map<ID, GoalProgressCount>;
};

// Hardcoded marketing dataset for the locked (non-premium) timeline — never touches real user data
export const buildTimelinePreviewData = (
  today: Timestamp,
): TimelinePreviewData => {
  const yearStart = startOfYear(today);

  return {
    lifeAreas: LIFE_AREAS.map((config) => makePreviewLifeArea(config, today)),
    goals: GOALS.map((config) => makePreviewGoal(config, yearStart, today)),
    goalProgressCounts: new Map<ID, GoalProgressCount>(
      GOALS.map((config) => [
        config.id,
        { completed: config.progress, total: 100 },
      ]),
    ),
  };
};
